import { useCallback, useEffect, useState } from 'preact/hooks'
import { nonProspectDomainsApi } from '@/api/nonProspectDomains'
import { useToast } from '@/hooks/useToast'
import type { NonProspectDomainsViewDto } from '@/types/nonProspectDomains'
import type { ApiResponse } from '@/api/client'

/** Which level's list this instance edits. */
export type NonProspectDomainsScope =
  | { level: 'company'; clientCompanyId: string }
  | { level: 'workspace'; clientCompanyId: string; workspaceId: string }

export interface UseNonProspectDomainsResult {
  view: NonProspectDomainsViewDto | null
  loading: boolean
  error: string | null
  /** Persists a replacement list. Returns the raw response so the editor can show a 422 inline. */
  save: (domains: string[]) => Promise<ApiResponse<NonProspectDomainsViewDto> | null>
}

/**
 * Loads and saves one level's non-prospect domain list (ORB-619).
 *
 * Saves persist per mutation, matching the approved-domains editor — there is no Save button, so a
 * half-entered list can never be what is stored. The ETag comes from the returned DTO rather than a
 * response header, because the sub-resource's ETag header is not CORS-exposed.
 */
export function useNonProspectDomains(scope: NonProspectDomainsScope): UseNonProspectDomainsResult {
  const { showErrorUnless422, showSuccess } = useToast()
  const [view, setView] = useState<NonProspectDomainsViewDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const scopeKey =
    scope.level === 'company'
      ? `company:${scope.clientCompanyId}`
      : `workspace:${scope.clientCompanyId}:${scope.workspaceId}`

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)

      const res =
        scope.level === 'company'
          ? await nonProspectDomainsApi.getForCompany(scope.clientCompanyId)
          : await nonProspectDomainsApi.getForWorkspace(scope.clientCompanyId, scope.workspaceId)

      if (cancelled) return

      if (res.data) {
        setView(res.data)
      } else {
        setError('Unable to load non-prospect domains.')
      }
      setLoading(false)
    }

    void load()
    return () => {
      cancelled = true
    }
    // scopeKey collapses the discriminated union into one stable dependency: `scope` is a fresh
    // object literal on every render, so depending on it directly would refetch forever.
  }, [scopeKey])

  const save = useCallback(
    async (domains: string[]) => {
      if (!view) return null

      const dto = { ETag: view.ETag, NonProspectDomains: domains }
      const res =
        scope.level === 'company'
          ? await nonProspectDomainsApi.patchForCompany(scope.clientCompanyId, dto)
          : await nonProspectDomainsApi.patchForWorkspace(scope.clientCompanyId, scope.workspaceId, dto)

      if (!res.data) {
        // A 422 (invalid entry / over the cap) is shown inline by the editor; anything else
        // (concurrency, network) needs a toast or the save fails silently.
        showErrorUnless422('Save failed', res, 'Unable to save non-prospect domains')
        return res
      }

      // Adopt the returned DTO wholesale — it carries the bumped ETag the next save needs.
      setView(res.data)
      showSuccess('Non-prospect domains saved', 'Audience views will update on the next refresh.')
      return res
    },
    // scopeKey stands in for `scope`, as above.
    [scopeKey, view, showErrorUnless422, showSuccess],
  )

  return { view, loading, error, save }
}
